import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  BookOpen, Users, GraduationCap, Shield, ShoppingBag, Crown, HelpCircle, User as UserIcon, Search,
  Printer, Download,
} from "lucide-react";
import { HELP_SECTIONS, HELP_TITLES, HELP_CONTENT } from "../i18n/userGuide";
import { escapeHtml as esc } from "../lib/safeHtml";

const ICONS = {
  overview:       BookOpen,
  parent:         Users,
  student:        GraduationCap,
  teacher:        UserIcon,
  school_admin:   Shield,
  canteen_staff:  ShoppingBag,
  super_admin:    Crown,
  faq:            HelpCircle,
};

/** Sections visible to each role.
 *  Parents see only the parent guide + overview + FAQ.
 *  Students see only the student guide + overview + FAQ.
 *  Staff see everything relevant to running the school. */
const SECTIONS_BY_ROLE = {
  parent:        ["overview", "parent", "faq"],
  student:       ["overview", "student", "faq"],
  teacher:       ["overview", "teacher", "faq"],
  canteen_staff: ["overview", "canteen_staff", "faq"],
  finance:       ["overview", "school_admin", "faq"],
  hr:            ["overview", "school_admin", "faq"],
  academic:      ["overview", "teacher", "school_admin", "faq"],
};

export default function HelpPage() {
  const { lang, dir } = useI18n();
  const { user } = useAuth();
  // Hooks MUST be called unconditionally (react-hooks/rules-of-hooks).
  // Compute visibleSections defensively (driver has empty list handled below).
  const visibleSections = SECTIONS_BY_ROLE[user?.role] || HELP_SECTIONS;
  const [params, setParams] = useSearchParams();
  const initialFromUrl = params.get("s");
  const initial = visibleSections.includes(initialFromUrl) ? initialFromUrl
                  : visibleSections.includes("overview") ? "overview"
                  : visibleSections[0];
  const [section, setSection] = useState(initial);
  const [q, setQ] = useState("");

  // Drivers don't get a help page — early return AFTER all hooks are called.
  if (user?.role === "driver") {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">لا يوجد دليل استخدام لهذا الحساب.</div>
      </div>
    );
  }


  const setActive = (s) => {
    setSection(s);
    setParams({ s });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fallbackLang = (key) => {
    const block = HELP_CONTENT[section];
    return (block && (block[lang] || block.en || block.ar)) || { intro: "", steps: [] };
  };
  const data = fallbackLang(section);

  // ---- Print / PDF helpers ----
  const printHTML = (sections) => {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    const cssDir = dir || "rtl";
    const css = `
      @page { size: A4; margin: 18mm 15mm; }
      body { font-family: "Segoe UI", "Cairo", "Helvetica", Arial, sans-serif; color: #111; line-height: 1.6; direction: ${cssDir}; padding: 0; }
      h1 { font-size: 26px; color: #15803d; border-bottom: 2px solid #15803d; padding-bottom: 8px; margin-top: 32px; }
      h2 { font-size: 18px; color: #166534; margin-top: 22px; }
      .intro { font-size: 14px; color: #4b5563; margin-bottom: 14px; }
      ol { padding-${cssDir === "rtl" ? "right" : "left"}: 24px; margin: 0; }
      li { margin: 10px 0; font-size: 13px; }
      li b { display: block; font-size: 14px; color: #0f172a; margin-bottom: 4px; }
      header { border-bottom: 3px solid #15803d; padding-bottom: 12px; margin-bottom: 18px; }
      header .brand { font-size: 22px; font-weight: 700; color: #15803d; }
      header .sub { font-size: 12px; color: #64748b; }
      footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
      .pagebreak { page-break-before: always; }
    `;
    const sectionHTML = sections.map((s, i) => {
      const d = HELP_CONTENT[s]?.[lang] || HELP_CONTENT[s]?.en || { intro: "", steps: [] };
      const title = HELP_TITLES[s][lang] || HELP_TITLES[s].en;
      return `
        <section class="${i > 0 ? "pagebreak" : ""}">
          <h1>${esc(title)}</h1>
          ${d.intro ? `<p class="intro">${esc(d.intro)}</p>` : ""}
          <ol>
            ${d.steps.map((st) => `<li><b>${esc(st.title)}</b><span>${esc(st.body)}</span></li>`).join("")}
          </ol>
        </section>
      `;
    }).join("");
    const docTitle = sections.length === 1
      ? (HELP_TITLES[sections[0]][lang] || HELP_TITLES[sections[0]].en)
      : (lang === "ar" ? "دليل استخدام Schoolin AI الكامل"
         : lang === "sv" ? "Schoolin AI Komplett användarguide"
         : lang === "da" ? "Schoolin AI Komplet brugervejledning"
         : "Schoolin AI — Complete User Guide");
    const footer = lang === "ar"
      ? "Schoolin AI — تم التوليد تلقائياً"
      : "Schoolin AI — Auto-generated";
    // XSS-safe: user/i18n-supplied values escaped; css + lang tokens are trusted internal constants.
    win.document.write(`
      <!doctype html><html lang="${esc(lang)}" dir="${esc(cssDir)}"><head>
      <meta charset="utf-8"><title>${esc(docTitle)}</title><style>${css}</style></head>
      <body>
        <header>
          <div class="brand">Schoolin AI · ${esc(docTitle)}</div>
          <div class="sub">${esc(new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : lang))}</div>
        </header>
        ${sectionHTML}
        <footer>${esc(footer)}</footer>
      </body></html>
    `);
    win.document.close();
    // Give it a moment to render fonts then trigger the native print dialog
    setTimeout(() => { try { win.focus(); win.print(); } catch (err) { console.error("print failed:", err); } }, 300);
  };

  const printSection = () => printHTML([section]);
  const downloadFull = () => printHTML(visibleSections);

  const norm = (s = "") => s.toLowerCase();
  const term = norm(q);
  const filteredSteps = term
    ? data.steps.filter((st) =>
        norm(st.title).includes(term) || norm(st.body).includes(term)
      )
    : data.steps;

  return (
    <div className="max-w-7xl mx-auto" data-testid="help-page">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-2">
          <BookOpen className="w-8 h-8 text-primary" />
          {HELP_TITLES.overview[lang] || "Documentation"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === "ar" && "دليل استخدام كامل بأربع لغات — يتغير المحتوى تلقائياً مع لغة الواجهة."}
          {lang === "en" && "Complete user guide in four languages — content auto-switches with your UI language."}
          {lang === "sv" && "Komplett användarguide på fyra språk — innehållet växlar automatiskt med UI-språket."}
          {lang === "da" && "Komplet brugervejledning på fire sprog — indholdet skifter automatisk med UI-sproget."}
        </p>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Side rail */}
        <Card className="p-3 border shadow-none h-fit sticky top-4">
          <div className="space-y-1">
            {visibleSections.map((s) => {
              const Icon = ICONS[s] || BookOpen;
              return (
                <button
                  key={s}
                  onClick={() => setActive(s)}
                  data-testid={`help-nav-${s}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-start ${
                    section === s
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{HELP_TITLES[s][lang] || HELP_TITLES[s].en}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Main content */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="relative max-w-md flex-1 min-w-[200px]">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "ar" ? "ابحث في هذا القسم..." : "Search this section..."}
                className="ps-10"
                data-testid="help-search"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={printSection} data-testid="help-print-section">
                <Printer className="w-4 h-4 me-1" />
                {lang === "ar" ? "طباعة هذا القسم"
                  : lang === "sv" ? "Skriv ut detta avsnitt"
                  : lang === "da" ? "Udskriv dette afsnit"
                  : "Print this section"}
              </Button>
              <Button size="sm" onClick={downloadFull} data-testid="help-download-full">
                <Download className="w-4 h-4 me-1" />
                {lang === "ar" ? "تحميل الدليل كاملاً"
                  : lang === "sv" ? "Hämta hela guiden"
                  : lang === "da" ? "Download hele guiden"
                  : "Download full guide"}
              </Button>
            </div>
          </div>

          <Card className="p-6 border shadow-none" data-testid={`help-section-${section}`}>
            <h2 className="text-2xl font-semibold mb-2">
              {HELP_TITLES[section][lang] || HELP_TITLES[section].en}
            </h2>
            {data.intro && (
              <p className="text-muted-foreground leading-relaxed">{data.intro}</p>
            )}
          </Card>

          {filteredSteps.length === 0 ? (
            <Card className="p-12 text-center border shadow-none text-muted-foreground">
              {lang === "ar" ? "لا نتائج" : "No results"}
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredSteps.map((st, i) => (
                <Card key={i} className="p-5 border shadow-none">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 text-primary font-bold w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-mono">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{st.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{st.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <Card className="p-4 border shadow-none bg-blue-50/40 dark:bg-blue-950/10 text-sm text-muted-foreground">
            {lang === "ar" && "💬 لم تجد إجابتك؟ تواصل مع مدير مدرستك مباشرة من قسم 'الرسائل'."}
            {lang === "en" && "💬 Didn't find your answer? Contact your school admin directly from 'Messages'."}
            {lang === "sv" && "💬 Hittade du inte svaret? Kontakta din skoladmin från 'Meddelanden'."}
            {lang === "da" && "💬 Fandt du ikke svaret? Kontakt din skoleadmin fra 'Beskeder'."}
          </Card>
        </div>
      </div>
    </div>
  );
}
